import { Analytics } from '@analytics';
import ButtonWithMenu from '@components/Common/ButtonWithMenu';
import CustomizableModal from '@components/Common/CustomizableModal';
import localizationMessages from '@components/Resources/ResourcesTable/messages';
import AccessControlledButton from '@components/TapToTip/AccessControlledButton';
import AccessControlledTableBody from '@components/TapToTip/AccessControlledTableBody';
import CreateTourModal from '@components/TapToTip/CreateTourModal';
import ManageGuidesModal from '@components/TapToTip/ManageGuidesModal';
import { StyleBreakpoints } from '@constants';
import { MenuItem, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import CustomSection, { SectionBadge } from '../CustomSection';
import { isTourActive, StatusBackgroundColor, StatusForeColor, StatusType } from './constants';
import { EMPTY_TOUR_FILTERS, filterTours, hasActiveTourFilters, tourCityOptions } from './filters';
import messages from './messages';
import ToursFilterBar from './ToursFilterBar';

const useStyles = makeStyles(theme => ({
  striped: {
    '& .MuiTableRow-root': {
      '&:nth-child(even)': {
        backgroundColor: '#FAF7FA'
      }
    },
    width: '100%'
  },
  tourTitle: {
    textOverflow: 'ellipsis',
    maxWidth: '100px',
    overflow: 'hidden',

    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      maxWidth: '150px'
    },

    [theme.breakpoints.up(StyleBreakpoints.md)]: {
      maxWidth: '200px'
    }
  }
}));

function mapTourForEditing(tour) {
  const lang = tour.translations.primaryLanguage;
  return {
    _id: tour._id,
    primaryLanguage: lang,
    title: tour.translations.translations[lang].data.title,
    description: tour.translations.translations[lang].data.longDescription,
    reviewSites: tour.reviewSites
  };
}

const TourStatus = ({ status }) => {
  return (
    <div
      className="text-center w-[86px] py-1 text-xs font-bold rounded-md"
      style={{
        backgroundColor: StatusBackgroundColor[status],
        color: StatusForeColor[status]
      }}>
      {StatusType[status]}
    </div>
  );
};

const ToursSection = ({
  tours,
  addTapToTipTour,
  editTapToTipTour,
  agentsProfiles,
  multipleAssign,
  deleteTour,
  isLoading
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const [isAddTourModalOpen, setIsAddTourModalOpen] = useState(false);
  const [isManageGuidesOpen, setIsManageGuidesOpen] = useState(false);
  const [tourDataToEdit, setTourDataToEdit] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tourToDelete, setTourToDelete] = useState({});
  // Held here rather than in the table so they survive opening and closing
  // the edit modal: edit a tour, save, and you are back on the same list.
  const [filters, setFilters] = useState(EMPTY_TOUR_FILTERS);
  const tableRef = useRef();

  const handleModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleTourRemove = tours => {
    deleteTour(tours._id);
    Analytics.track('delete tour clicked');
    handleModalClose();
  };

  function openEditTourModal(data) {
    setTourDataToEdit(mapTourForEditing(data));
    setIsAddTourModalOpen(true);
  }

  function openAddTourModal() {
    setTourDataToEdit(null);
    setIsAddTourModalOpen(true);
    Analytics.track('create TTT tour opened');
  }

  const getMenuBody = rowData => [
    <AccessControlledButton
      key={rowData._id}
      feature="PostTipReviewCollection"
      as="MenuItem"
      title={intl.formatMessage(messages.editTour)}
      onClick={() => {
        openEditTourModal(rowData);
        Analytics.track('edit TTT tour opened');
      }}
    />,
    <MenuItem
      key={rowData._id}
      onClick={() => {
        setIsDeleteModalOpen(true);
        setTourToDelete(rowData);
        Analytics.track('delete TTT tour opened');
      }}>
      {intl.formatMessage(messages.deleteTour)}
    </MenuItem>
  ];

  const columns = [
    {
      field: 'status',
      title: intl.formatMessage(messages.status),
      customSort: (a, b) => {
        return isTourActive(a) - isTourActive(b);
      },
      render: rowData => (
        <TourStatus status={isTourActive(rowData) ? 'active' : 'incomplete'} />
      )
    },
    {
      field: 'title',
      title: intl.formatMessage(messages.tour),
      render: rowData => {
        return (
          <div className={classes.tourTitle} title={rowData.title}>
            {rowData.title}
          </div>
        );
      }
    },
    {
      field: 'city',
      title: 'City',
      render: rowData => {
        const city = rowData.city
          ? rowData.city.charAt(0).toUpperCase() + rowData.city.slice(1)
          : '—'; // em dash as fallback

        return <div className={classes.tourTitle}>{city}</div>;
      }
    },
    {
      field: 'numberTourSites',
      title: intl.formatMessage(messages.reviewLinks),
      defaultSort: 'desc',
      render: rowData => {
        return (
          <div className="text-center w-[86px] py-1 text-xs font-bold rounded-md text-slate-600">
            {rowData.numberTourSites} {rowData.numberTourSites > 1 ? 'Links' : 'Link'}
          </div>
        );
      }
    },
    {
      title: intl.formatMessage(messages.manage),
      field: 'manage',
      sorting: false,
      headerStyle: {
        paddingRight: '25px',
        textAlign: 'right'
      },
      cellStyle: {
        textAlign: 'right'
      },
      render: rowData => <ButtonWithMenu modern menuBody={getMenuBody(rowData)} />
    }
  ];

  const toursWithTitles = useMemo(
    () =>
      tours?.map(tour => {
        return {
          ...tour,
          title: tour.translations.translations[tour.translations.primaryLanguage].data.title,
          numberTourSites: tour.reviewSites
            ? tour.reviewSites.filter(site => site.url && site.url.length).length
            : 0
        };
      }),
    [tours]
  );

  const cityOptions = useMemo(() => tourCityOptions(toursWithTitles), [toursWithTitles]);
  const filteredTours = useMemo(() => filterTours(toursWithTitles, filters), [
    toursWithTitles,
    filters
  ]);
  const isFiltering = hasActiveTourFilters(filters);

  // A city that no tour has any more (renamed or deleted) would otherwise
  // filter the table to nothing with no way to see why.
  useEffect(() => {
    if (filters.city && !cityOptions.some(city => city.value === filters.city)) {
      setFilters(current => ({ ...current, city: '' }));
    }
  }, [cityOptions, filters.city]);

  // material-table only clamps to the last page when the data shrinks, so a
  // search typed on page 7 would land on the last page of results. Back to
  // the first page whenever the filters change.
  useEffect(() => {
    tableRef.current?.onChangePage?.(null, 0);
  }, [filters]);

  return (
    <>
      <CustomSection
        id="Tours"
        badge={SectionBadge.PRO}
        title={intl.formatMessage(messages.tours)}
        description={intl.formatMessage(messages.description)}
        feature="PostTipReviewCollection"
        shouldCheckFeature
        isForm={false}>
        {toursWithTitles?.length > 0 && (
          <ToursFilterBar
            filters={filters}
            setFilters={setFilters}
            cityOptions={cityOptions}
            shown={filteredTours.length}
            total={toursWithTitles.length}
          />
        )}
        <MaterialTable
          tableRef={tableRef}
          isLoading={isLoading}
          className={classes.table}
          title={intl.formatMessage(messages.tours)}
          columns={columns}
          data={filteredTours}
          components={{
            Container: props => (
              <Paper
                className={`${classes.striped} !rounded-2xl border border-solid border-slate-200 !shadow-sm overflow-hidden`}
                {...props}
              />
            )
          }}
          options={{
            pageSize: 10,
            pageSizeOptions: [10, 25, 50, 100],
            tableLayout: 'auto',
            emptyRowsWhenPaging: false,
            headerStyle: {
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: '0'
            },
            draggable: false,
            cellStyle: {
              whiteSpace: 'nowrap',
              width: 'fit-content'
            },
            toolbar: false,
            maxBodyHeight: window.innerWidth > StyleBreakpoints.sm ? '75vh' : '55vh'
          }}
          localization={{
            pagination: {
              labelRowsSelect: intl.formatMessage(localizationMessages.labelRowsSelect),
              labelRowsPerPage: intl.formatMessage(localizationMessages.labelRowsPerPage),
              firstAriaLabel: intl.formatMessage(localizationMessages.firstAriaLabel),
              firstTooltip: intl.formatMessage(localizationMessages.firstTooltip),
              previousAriaLabel: intl.formatMessage(localizationMessages.previousAriaLabel),
              previousTooltip: intl.formatMessage(localizationMessages.previousTooltip),
              nextAriaLabel: intl.formatMessage(localizationMessages.nextAriaLabel),
              nextTooltip: intl.formatMessage(localizationMessages.nextTooltip),
              lastAriaLabel: intl.formatMessage(localizationMessages.lastAriaLabel),
              lastTooltip: intl.formatMessage(localizationMessages.lastTooltip)
            },
            body: {
              emptyDataSourceMessage: isFiltering ? (
                <div className="py-8 text-center" data-testid="tours-no-matches">
                  <p className="font-bold text-slate-900">
                    {intl.formatMessage(messages.noMatchesTitle)}
                  </p>
                  <p className="text-sm text-slate-600">
                    {intl.formatMessage(messages.noMatchesDescription)}
                  </p>
                </div>
              ) : (
                <AccessControlledTableBody
                  feature="PostTipReviewCollection"
                  title={intl.formatMessage(messages.emptyBodyTitle)}
                  description={intl.formatMessage(messages.emptyBodyDescription)}
                />
              )
            }
          }}
        />

        {/* data-whats-new: the What's new tour for importing a tour from a
            website points here (backend whats_new catalogue). */}
        <div data-whats-new="add-tour">
          <AccessControlledButton
            elementStyle="flex w-full !mt-5"
            feature="PostTipReviewCollection"
            tooltipTitle="Create new tours to collect reviews for"
            title={intl.formatMessage(messages.createTour)}
            onClick={openAddTourModal}
          />
        </div>
      </CustomSection>

      <CreateTourModal
        isOpen={isAddTourModalOpen}
        setIsOpen={setIsAddTourModalOpen}
        addTapToTipTour={addTapToTipTour}
        setIsManageGuidesOpen={setIsManageGuidesOpen}
        updateTapToTipTour={editTapToTipTour}
        tourDetails={tourDataToEdit}
      />
      <ManageGuidesModal
        isOpen={isManageGuidesOpen}
        setIsOpen={setIsManageGuidesOpen}
        setIsAddTourModalOpen={setIsAddTourModalOpen}
        guides={agentsProfiles}
        tourDetails={tourDataToEdit}
        multipleAssign={multipleAssign}
      />
      <CustomizableModal
        modern
        isOpen={isDeleteModalOpen}
        onClose={handleModalClose}
        handleHeaderCloseButton={handleModalClose}
        title={intl.formatMessage(messages.confirmAction)}
        body={
          <p className="text-slate-600">
            {intl.formatMessage(messages.removeTourConfirm)}:{' '}
            <b className="font-bold text-slate-900">{tourToDelete.title}</b>
          </p>
        }
        handleFooterCloseButton={handleModalClose}
        primaryActionButtonData={{
          content: intl.formatMessage(messages.confirm),
          onClick: () => handleTourRemove(tourToDelete),
          isDisabled: false
        }}
      />
    </>
  );
};

export default ToursSection;
